// Color roles (left = worse for sequential-higher; bipolar continues through green into blue)
// ridiculous_bad full red — beyond anything you'd normally see bad_reasonable slightly less red —
// bad, but a value real fields actually hit deeply_unhealthy orange — clearly unhealthy
// edge_healthy greenish-yellow — barely on the edge of healthy (often the critical level)
// good_reasonable slightly less green — good and typical ridiculous_good full green — luxury /
// ideal (the peak on a bipolar layer) high_reasonable light blue — bipolar only: above ideal, still
// plausible ridiculous_high full blue — bipolar only: ridiculously high Soil numbers are the
// platform's stored basis: Mehlich-3, 8″, ppm (CEC meq/100g, OM %, pH unitless).
import type { ManifestLayer } from "../types/contract";
import { BUNDLED_REFERENCE } from "./referenceData";

export const STOP_ROLES = [
  "ridiculous_bad",
  "bad_reasonable",
  "deeply_unhealthy",
  "edge_healthy",
  "good_reasonable",
  "ridiculous_good",
  "high_reasonable",
  "ridiculous_high",
] as const;
export type StopRole = (typeof STOP_ROLES)[number];
export const SEQ_ROLES: StopRole[] = [
  "ridiculous_bad",
  "bad_reasonable",
  "deeply_unhealthy",
  "edge_healthy",
  "good_reasonable",
  "ridiculous_good",
];
export const HIGH_ROLES: StopRole[] = ["high_reasonable", "ridiculous_high"];

export const SCALE_COLORS: Record<StopRole, string> = {
  ridiculous_bad: "#a50026",
  bad_reasonable: "#d73027",
  deeply_unhealthy: "#f46d43",
  edge_healthy: "#fee08b",
  good_reasonable: "#66bd63",
  ridiculous_good: "#1a9850",
  high_reasonable: "#74add1",
  ridiculous_high: "#313695",
};

export type ScaleShape = "sequential" | "bipolar";
export type ScaleDirection = "higher" | "lower" | "from_layer";

export interface AbsoluteScale {
  id: string;
  title: string;
  unit: string;
  basis: string;
  shape: ScaleShape;
  direction: ScaleDirection;
  notes: string;
  values: Partial<Record<StopRole, number>>;
}

export interface ColorStop {
  role: StopRole;
  value: number;
  color: string;
}

export interface ResolvedScale {
  scale: AbsoluteScale;
  stops: ColorStop[];
  shape: ScaleShape;
}

export const SCALES: AbsoluteScale[] = [
  ...BUNDLED_REFERENCE["color-scales"].scales,
] as AbsoluteScale[];

export function installColorScales(doc: { scales: AbsoluteScale[] }): void {
  SCALES.splice(0, SCALES.length, ...doc.scales);
}

export const SCALE_BY_ID = {
  get(id: string): AbsoluteScale | undefined {
    return SCALES.find((scale) => scale.id === id);
  },
};

const HANEY_KEY_TO_SCALE: Record<string, string> = {
  soil_health_score: "haney_shc",
  co2c: "haney_co2c",
  weoc: "haney_weoc",
  weon: "haney_weon",
  weoc_weon: "haney_cn",
  inorganic_n: "haney_inorg_n",
  inorganic_p: "haney_inorg_p",
  total_p: "haney_total_p",
  organic_p: "haney_org_p",
  h3a_k: "haney_k",
  mac_pct: "haney_mac",
  ph: "ph",
  available_n_lb_ac: "haney_avail_n",
  available_p_lb_ac: "haney_avail_p",
  available_k_lb_ac: "haney_avail_k",
};

const SOIL_KEYS_LONG_FIRST = [
  "buffer_ph",
  "k_sat_pct",
  "ca_sat_pct",
  "mg_sat_pct",
  "h_sat_pct",
  "na_sat_pct",
  "base_sat_pct",
  "ph",
  "om",
  "cec",
  "ca",
  "mg",
  "zn",
  "mn",
  "p",
  "k",
  "s",
  "b",
] as const;

const SOIL_KEY_TO_SCALE: Record<string, string> = {
  ph: "ph",
  buffer_ph: "buffer_ph",
  om: "om",
  cec: "cec",
  p: "p",
  k: "k",
  s: "s",
  ca: "ca",
  mg: "mg",
  zn: "zn",
  b: "b",
  mn: "mn",
  k_sat_pct: "k_sat",
  ca_sat_pct: "ca_sat",
  mg_sat_pct: "mg_sat",
  h_sat_pct: "h_sat",
  na_sat_pct: "na_sat",
  base_sat_pct: "base_sat",
};

function soilKeyFromLayerId(id: string): string | null {
  const base = id.replace(/-8in$/, "");
  // The raster key is `bph`. The authored scale id is `buffer_ph`. The long-key
  // loop only knows the scale id, so `ml-bph` would miss it and the title
  // "Buffer pH" would later match water pH.
  if (base === "ml-bph") return "buffer_ph";
  for (const k of SOIL_KEYS_LONG_FIRST) {
    if (base === `soil-${k}` || base === `ml-${k}`) return k;
    if (
      base.startsWith("soil-old-") &&
      (base.endsWith(`-${k}`) || base.endsWith(`-${k}-8in`))
    )
      return k;
  }
  if (id === "ml-map") return "ph";
  return null;
}

function yieldScaleFromHay(hay: string): string {
  if (/\bsoy|soybean|\bsoya\b/.test(hay)) return "yield_soy";
  if (/\bwheat|small.?grain/.test(hay)) return "yield_wheat";
  return "yield_corn";
}

function seedScaleFromHay(hay: string): string {
  if (/\bsoy|soybean|\bsoya\b/.test(hay)) return "seedrate_soy";
  return "seedrate_corn";
}

function fertScaleFromHay(id: string, hay: string): string {
  if (/lime/.test(id) || /\blime\b/.test(hay)) return "fert_lime";
  if (
    /^autorx-p($|-)/.test(id) ||
    /^fert-p2o5/.test(id) ||
    /(^|-)p2o5($|-)|p₂o₅|\bdap\b/.test(`${id} ${hay}`)
  )
    return "fert_p2o5";
  if (
    /^autorx-k($|-)/.test(id) ||
    /^fert-k2o/.test(id) ||
    /(^|-)k2o($|-)|k₂o|\bpotash\b|\bmop\b|\bsop\b/.test(`${id} ${hay}`)
  )
    return "fert_k2o";
  if (/(^|-)mg($|-)|magnesium/.test(id) || /\bmg rec\b/.test(hay))
    return "fert_mg";
  return "spread_rate";
}

/*
 * Same token rules as `blobPropOf` (pH before P, saturation percents excluded) — kept local so this
 * module stays import-clean (no soilUnits/store/config).
 */
export function soilPropOf(
  keyOrTitle: string,
): "ph" | "om" | "cec" | "p" | "k" | "ca" | "mg" | null {
  const s = keyOrTitle
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return null;
  // A stray "Buffer pH" / `bph` title must not fall through onto the water-pH scale.
  if (/\bbuffer ph\b|\bbph\b/.test(s)) return null;
  if (/\bph\b/.test(s)) return "ph";
  if (/\bom\b|organic matter/.test(s)) return "om";
  if (/\bcec\b/.test(s)) return "cec";
  if (/\bp\b|phosph/.test(s)) return "p";
  if (/\bk\b|potass/.test(s)) return "k";
  const tokens = s.split(" ").map((t) => t.replace(/[()]/g, ""));
  if (tokens.includes("ca") || tokens.includes("calcium")) return "ca";
  if (tokens.includes("mg") || tokens.includes("magnesium")) return "mg";
  return null;
}

/* * Which authored scale a Visualize layer should use, or null to keep the field-relative ramp. */
export function scaleIdForLayer(
  layer: Pick<
    ManifestLayer,
    "id" | "title" | "unit" | "colormap" | "rgb" | "categories" | "stretch"
  >,
): string | null {
  // "Exaggerate colors": the layer's own min..max, so no authored scale applies
  if (layer.stretch) return null;
  if (layer.rgb) return null;
  if (layer.categories && layer.categories.length > 0) return null;
  const cm = (layer.colormap ?? "").toLowerCase();
  if (cm === "rdbu" || cm === "diverging") return null;

  let id = (layer.id ?? "").toLowerCase();
  // Visualize paints the sample-kriging underlay as `sample-krige-soil-p-8in`. That id missed every
  // soil-* / ml-* matcher, so the cells either fell through to a 0–1 ramp (every real ppm clamps
  // to full green) or to ppm_generic — never the same scale as the dots they sit under.
  while (id.startsWith("sample-krige-")) id = id.slice("sample-krige-".length);
  const key = id.replace(/^cov-/, "");
  const title = (layer.title ?? "").toLowerCase();
  const unit = (layer.unit ?? "").toLowerCase();
  const hay = `${id} ${key} ${title}`.replace(/[_-]+/g, " ");

  // Composition / clay-group % of a map unit is not a quality metric — leave field-relative.
  // soilcomp__clay_* is mineralogy share, not the SSURGO texture clay scale.
  if (
    id.startsWith("comp-") ||
    id.startsWith("clayfn-") ||
    key.startsWith("soilcomp__pct_") ||
    key.startsWith("soilcomp__clay_")
  )
    return null;

  const soilKey = soilKeyFromLayerId(id);
  if (soilKey) return SOIL_KEY_TO_SCALE[soilKey] ?? null;
  // The stack covariate `sample__idw_bph` is buffer pH too. Its "Buffer pH" title
  // no longer reads as water pH, so without this it would drop to field-relative.
  if (key === "sample__idw_bph") return "buffer_ph";

  if (id.startsWith("haney-")) {
    const hk = id.slice("haney-".length);
    return HANEY_KEY_TO_SCALE[hk] ?? null;
  }
  if (
    id === "kfix" ||
    key === "soilcomp__kfix_gross_pct" ||
    /\bk fixation\b/.test(hay)
  )
    return "kfix";
  if (id === "spread-rate" || id.startsWith("spread-"))
    return fertScaleFromHay(id, hay);
  if (id.startsWith("fert-") || id.startsWith("autorx-"))
    return fertScaleFromHay(id, hay);

  if (id === "yield-preview" || /yield \(visible/.test(title))
    return yieldScaleFromHay(hay);

  // Modeled / IDW / check-accuracy twins — tokenise through blobPropOf so `p` never eats `ph`.
  if (
    /^(ml-|model__|sample__idw_|automap__grid_|check-sample-|check-recreated-)/.test(
      id,
    ) ||
    /^(model__uncorrected_|sample__idw_|automap__grid_)/.test(key)
  ) {
    const prop = soilPropOf(id) ?? soilPropOf(key) ?? soilPropOf(title);
    if (prop === "ph") return "ph";
    if (prop === "om") return "om";
    if (prop === "cec") return "cec";
    if (prop === "p") return "p";
    if (prop === "k") return "k";
    if (prop === "ca") return "ca";
    if (prop === "mg") return "mg";
  }

  if (/fertility__index/.test(key) && !/_pct$/.test(key))
    return "fertility_index";
  if (
    /productivity__index/.test(key) &&
    !/_pct$/.test(key) &&
    !/_vs_/.test(key)
  )
    return "productivity_index";
  if (
    /_pct$/.test(key) ||
    /%ile|percentile/.test(unit) ||
    /percentile/.test(title)
  )
    return "percentile";
  if (/__z$|_z$|\/ *z-score|\bz score\b|\bz-score\b/.test(key + " " + title))
    return "zscore";

  if (/\bndvi\b/.test(hay)) return "ndvi";
  if (/\btwi\b|wetness index/.test(hay)) return "twi";
  if (/\bkfactor|\bkffact\b|erodib/.test(hay)) return "kfactor";
  if (/\bbulk\s*dens|\bbd_/.test(hay)) return "bulk_density";
  if (/\bksat\b/.test(hay)) return "ksat";
  if (/\bawc\b|available water/.test(hay)) return "awc";
  if (/\bclay\b/.test(hay) && (unit === "%" || /ssurgo|texture/.test(hay)))
    return "clay";
  if (/\bsand\b/.test(hay) && (unit === "%" || /ssurgo|texture/.test(hay)))
    return "sand";
  if (/\bsilt\b/.test(hay) && (unit === "%" || /ssurgo|texture/.test(hay)))
    return "silt";
  if (/slope/.test(hay) && !/position/.test(hay)) {
    if (unit === "degrees" || /degrees/.test(hay)) return "slope_deg";
    return "slope_pct";
  }
  if (/\bph\b/.test(hay) && !/buffer ph|\bbph\b|basesat_ph|phosph/.test(hay))
    return "ph";
  if (/\bom\b|organic matter/.test(hay)) return "om";
  if (/\bcec\b/.test(hay)) return "cec";

  if (
    /diffusion|supply/.test(hay) &&
    /\bp\b|phosph/.test(hay) &&
    !/variance/.test(hay)
  )
    return "diffusion_p";
  if (
    /diffusion|supply/.test(hay) &&
    /\bk\b|potass/.test(hay) &&
    !/variance/.test(hay)
  )
    return "diffusion_k";

  if (/seedrate|seeding rate|population|seeds\/ac/.test(hay))
    return seedScaleFromHay(hay);
  if (
    /appl|as-applied|as applied|n_lbs|fert_total/.test(hay) &&
    (unit.includes("lb") || /lb\/ac/.test(unit))
  )
    return "appl_n";
  if (/tilldepth|tillage depth/.test(hay)) return "till_depth";
  if (/\bec\b|electrical conductivity|salin/.test(hay)) {
    if (unit.includes("ds") || /ds\/m/.test(unit)) return "ec_saline";
    return "ec_scan";
  }

  if (/\byield\b/.test(hay) || unit === "bu/ac" || unit === "bu / ac")
    return yieldScaleFromHay(hay);
  if (/\bprecip|rainfall|gsprecip/.test(hay))
    return unit === "in" || unit === "inches" ? "precip_in" : "precip_mm";
  if (/\bdeficit\b/.test(hay)) return "deficit_mm";
  if (/\bheat days\b|\bheat_days\b/.test(hay)) return "heat_days";
  if (/\bfreeze|frost/.test(hay) && /days/.test(hay)) return "freeze_days";
  if (/dry.?spell/.test(hay)) return "dry_spell";
  if (/\bgdd\b|growing degree/.test(hay)) return "gdd";
  if (/\btmax\b|max temp/.test(hay)) return "tmax";
  if (/cover.?crop|cover_window/.test(hay) && (unit === "" || unit === "%"))
    return "cover_prob";
  if (/rotation.?divers/.test(hay)) return "rotation_div";
  if (/corn.on.corn|corn_on_corn/.test(hay)) return "corn_on_corn";

  if (unit === "%" || unit === "percent") return "percent_0_100";
  if (unit === "ppm") return "ppm_generic";
  if (unit === "meq/100g" || unit === "cmol/kg") return "cec";
  if (unit === "mm" && /et\b|evapo/.test(hay)) return "deficit_mm";

  return null;
}

export function scaleForLayer(
  layer: Pick<
    ManifestLayer,
    "id" | "title" | "unit" | "colormap" | "rgb" | "categories" | "stretch"
  >,
): AbsoluteScale | null {
  const id = scaleIdForLayer(layer);
  return id ? (SCALE_BY_ID.get(id) ?? null) : null;
}

function resolvedDirection(
  scale: AbsoluteScale,
  layer: Pick<ManifestLayer, "direction">,
): "higher" | "lower" {
  if (scale.direction === "from_layer") {
    if (layer.direction === "lower") return "lower";
    return "higher";
  }
  return scale.direction;
}

function uniqueRising(stops: ColorStop[]): ColorStop[] {
  const sorted = [...stops]
    .filter((s) => Number.isFinite(s.value))
    .sort((a, b) => a.value - b.value);
  const out: ColorStop[] = [];
  for (const s of sorted) {
    if (out.length && s.value <= out[out.length - 1]!.value) continue;
    out.push(s);
  }
  return out;
}

/* * Color stops in numeric order, ready for MapLibre interpolate / a LUT. */
export function resolveScale(
  layer: Pick<
    ManifestLayer,
    | "id"
    | "title"
    | "unit"
    | "colormap"
    | "rgb"
    | "categories"
    | "direction"
    | "stretch"
  >,
): ResolvedScale | null {
  const scale = scaleForLayer(layer);
  if (!scale) return null;
  const dir = resolvedDirection(scale, layer);
  const roles: StopRole[] =
    scale.shape === "bipolar" ? [...SEQ_ROLES, ...HIGH_ROLES] : [...SEQ_ROLES];
  let stops: ColorStop[] = roles
    .filter((r) => scale.values[r] != null && Number.isFinite(scale.values[r]))
    .map((r) => ({
      role: r,
      value: scale.values[r] as number,
      color: SCALE_COLORS[r],
    }));
  // Role colors are authored worse→better (red→green). Lower-is-better already stores the
  // worst number on ridiculous_bad (a HIGH rec / steep slope). Sorting by value then puts
  // green at the numeric floor. `dir` is kept so from_layer percentiles can reverse colors.
  if (dir === "lower" && scale.direction === "from_layer") {
    const values = uniqueRising(stops).map((s) => s.value);
    const colors = [...stops]
      .sort((a, b) => a.value - b.value)
      .map((s) => s.color)
      .reverse();
    stops = values.map((value, i) => ({
      role: SEQ_ROLES[i] ?? "ridiculous_good",
      value,
      color: colors[i]!,
    }));
  } else {
    stops = uniqueRising(stops);
  }
  if (stops.length < 2) return null;
  return { scale, stops, shape: scale.shape };
}

export function absoluteColorStops(
  layer: Pick<
    ManifestLayer,
    | "id"
    | "title"
    | "unit"
    | "colormap"
    | "rgb"
    | "categories"
    | "direction"
    | "stretch"
  >,
): ColorStop[] | null {
  return resolveScale(layer)?.stops ?? null;
}

/* * Endpoints the legend prints — absolute when we have a scale, else the layer's own min/max. */
export function legendNumericRange(layer: ManifestLayer): {
  min: number;
  max: number;
  ideal?: number;
} {
  const resolved = resolveScale(layer);
  if (resolved) {
    const min = resolved.stops[0]!.value;
    const max = resolved.stops[resolved.stops.length - 1]!.value;
    const ideal =
      resolved.shape === "bipolar"
        ? resolved.scale.values.ridiculous_good
        : undefined;
    return { min, max, ideal };
  }
  return { min: layer.value_min ?? 0, max: layer.value_max ?? 1 };
}

/*
 * Feature property the choropleth should read. Sample layers bake display `value` and stored `value_stored`.
 * `has` (not coalesce): a missing `value_stored` on kriged cells must not win as 0 / NaN — that
 * clamped every cell to one end of the ramp (all-green or all-red) while the dots still varied.
 */
export function paintValueExpression(fallback: number): unknown[] {
  return [
    "to-number",
    [
      "case",
      ["has", "value_stored"],
      ["get", "value_stored"],
      ["get", "value"],
    ],
    fallback,
  ];
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h,
    16,
  );
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbAtStops(
  stops: ColorStop[],
  v: number,
): [number, number, number] {
  if (v <= stops[0]!.value) return hexToRgb(stops[0]!.color);
  const last = stops[stops.length - 1]!;
  if (v >= last.value) return hexToRgb(last.color);
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!,
      b = stops[i + 1]!;
    if (v > b.value) continue;
    const t = (v - a.value) / (b.value - a.value || 1);
    const ca = hexToRgb(a.color),
      cb = hexToRgb(b.color);
    return [
      Math.round(ca[0] + (cb[0] - ca[0]) * t),
      Math.round(ca[1] + (cb[1] - ca[1]) * t),
      Math.round(ca[2] + (cb[2] - ca[2]) * t),
    ];
  }
  return hexToRgb(last.color);
}
